import type { Locale } from 'antd/es/locale';
import { Capacitor } from '@capacitor/core';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { initTranslation, getAntdLocale } from 'src/modules/i18n';
import { initAxios } from 'src/modules/axios';
import {
  applyPreviewStackFromUrl,
  guardActiveStackHealth,
} from 'src/modules/previewStack';

// Backstop only.
export const LOCAL_INIT_TIMEOUT_MS = 15_000;

export const runLocalInit = async (
  onLocale: (locale: Locale) => void,
): Promise<void> => {
  const work = async () => {
    // MUST precede initAxios(): initAxios captures axios.defaults.baseURL once, so the ?stack=
    // override has to be persisted first or relative /api/* calls would hit the stale default
    // backend. Both are inert in production (VITE_STACK_MANAGER_URL='').
    await applyPreviewStackFromUrl();
    await guardActiveStackHealth();

    if (Capacitor.isPluginAvailable('SafeArea')) {
      await import('@capacitor-community/safe-area');
    }

    await defineCustomElements(window);
    await initTranslation();
    onLocale(await getAntdLocale());
    initAxios();
  };

  let timeoutId: ReturnType<typeof setTimeout>;
  await Promise.race([
    work().finally(() => clearTimeout(timeoutId)),
    new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('Local init timed out')),
        LOCAL_INIT_TIMEOUT_MS,
      );
    }),
  ]);
};
